import { getStoredToken, refreshAccessToken } from './authApi';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

export interface TeamMember {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at?: string;
  permissions: Record<string, boolean>;
}

export interface CreateMemberRequest {
  name: string;
  email_prefix: string;
  role: string;
  password: string;
  outlet_ids?: number[];
}

// Centralized error extraction — reads FastAPI's { detail: "..." } body when
// present, falls back to a generic message only if the response truly has
// no parseable JSON. This is what was missing everywhere except createTeamMember.
async function extractError(res: Response, fallback: string): Promise<string> {
  try {
    const data = await res.json();
    if (data?.detail) {
      // FastAPI validation errors (422) return detail as an array of
      // objects rather than a string — handle both shapes.
      if (Array.isArray(data.detail)) {
        return data.detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
      }
      return data.detail;
    }
    return fallback;
  } catch {
    return fallback;
  }
}

// Same silent-refresh-on-401 pattern as utils/api.ts and utils/client.ts —
// keeps IAM in parity with the rest of the app instead of erroring out on
// every action once the access token expires.
async function iamFetch(path: string, options: RequestInit = {}, _isRetry = false): Promise<Response> {
  const token = getStoredToken();
  const res = await fetch(`${BACKEND}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401 && !_isRetry) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      return iamFetch(path, options, true);
    }
  }

  return res;
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const res = await iamFetch('/iam/users');
  if (!res.ok) throw new Error(await extractError(res, 'Failed to fetch team'));
  return res.json();
}

export async function createTeamMember(data: CreateMemberRequest): Promise<TeamMember> {
  const res = await iamFetch('/iam/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractError(res, 'Failed to create member'));
  return res.json();
}

export async function updateRole(userId: number, role: string): Promise<void> {
  const res = await iamFetch(`/iam/users/${userId}/role`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
  if (!res.ok) throw new Error(await extractError(res, 'Failed to update role'));
}

export async function resetPassword(userId: number, password: string): Promise<void> {
  const res = await iamFetch(`/iam/users/${userId}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  });
  if (!res.ok) throw new Error(await extractError(res, 'Failed to reset password'));
}

export async function deleteUser(userId: number): Promise<void> {
  const res = await iamFetch(`/iam/users/${userId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await extractError(res, 'Failed to delete user'));
}

export async function fetchPermissions(userId: number): Promise<Record<string, boolean>> {
  const res = await iamFetch(`/iam/users/${userId}/permissions`);
  if (!res.ok) throw new Error(await extractError(res, 'Failed to fetch permissions'));
  const data = await res.json();
  return data.permissions;
}

export async function savePermissions(userId: number, perms: Record<string, boolean>): Promise<void> {
  const res = await iamFetch(`/iam/users/${userId}/permissions`, {
    method: 'PUT',
    body: JSON.stringify(perms),
  });
  if (!res.ok) throw new Error(await extractError(res, 'Failed to save permissions'));
}